import { useState } from 'react'
import { Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { useCurrency } from '@/contexts/CurrencyContext'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { printShiftReport } from '@/lib/printShiftReport'
import type { ShiftClosing } from '@/types'

interface ShiftReportButtonProps {
  shift: ShiftClosing
  businessDate: string
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'sm' | 'default' | 'lg'
  label?: string
}

/**
 * Prints one shift's handover slip (F-CTRL-N29). Sibling of ZReportButton:
 * fetches receipt branding, then dispatches an off-screen iframe print job.
 */
export function ShiftReportButton({
  shift,
  businessDate,
  variant = 'ghost',
  size = 'sm',
  label = 'Print',
}: ShiftReportButtonProps) {
  const { formatCurrency } = useCurrency()
  const [busy, setBusy] = useState(false)

  const handleClick = async () => {
    if (busy) return
    setBusy(true)
    try {
      const settingsRes = await apiClient.getAllSettings()
      if (!settingsRes.success || !settingsRes.data) {
        toastHelpers.error(settingsRes.message || 'Failed to load receipt settings')
        return
      }
      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      const { printed } = printShiftReport(shift, businessDate, cfg, formatCurrency)
      if (!printed) {
        toastHelpers.error('Print failed — check printer connection and pop-up settings')
      }
    } catch (err) {
      toastHelpers.apiError('Print shift report', err as Error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button type="button" variant={variant} size={size} onClick={handleClick} disabled={busy}>
      <Printer className="mr-1.5 h-3.5 w-3.5" />
      {busy ? 'Preparing…' : label}
    </Button>
  )
}
